export const ADSENSE_CLIENT = "ca-pub-8691062301030113";
export const ADSENSE_SCRIPT_URL = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${ADSENSE_CLIENT}`;
export const ADSENSE_IN_FEED_LAYOUT_KEY = "-6t+ed+2i-1n-4w";

let scriptLoadPromise = null;

function markScriptLoaded(script) {
  if (script) {
    script.dataset.loaded = "true";
  }
}

function isScriptReady(script) {
  return (
    script?.dataset?.loaded === "true" ||
    (typeof window !== "undefined" && Array.isArray(window.adsbygoogle))
  );
}

export function ensureAdSenseScript() {
  if (typeof window === "undefined") {
    return Promise.resolve();
  }

  // Avoid injecting AdSense on localhost — failed loads show as opaque "Script error."
  if (process.env.NODE_ENV === "development") {
    return Promise.resolve();
  }

  window.adsbygoogle = window.adsbygoogle || [];

  if (scriptLoadPromise) {
    return scriptLoadPromise;
  }

  scriptLoadPromise = new Promise((resolve) => {
    const existing = document.querySelector(
      'script[data-wlj-ads], script[src*="pagead2.googlesyndication.com"]'
    );

    const markLoaded = () => resolve();

    if (existing) {
      if (isScriptReady(existing)) {
        markScriptLoaded(existing);
        markLoaded();
        return;
      }

      existing.addEventListener("load", () => {
        markScriptLoaded(existing);
        markLoaded();
      }, { once: true });

      existing.addEventListener("error", markLoaded, { once: true });

      if (existing.readyState === "complete" || existing.readyState === "loaded") {
        markScriptLoaded(existing);
        markLoaded();
      }
      return;
    }

    const script = document.createElement("script");
    script.async = true;
    script.setAttribute("data-wlj-ads", "true");
    script.src = ADSENSE_SCRIPT_URL;
    script.onload = () => {
      markScriptLoaded(script);
      markLoaded();
    };
    script.onerror = markLoaded;
    document.body.appendChild(script);
  });

  return scriptLoadPromise;
}

export function pushAdSenseAd(adElement) {
  if (!adElement || adElement.getAttribute("data-adsense-initialized") === "true") {
    return false;
  }

  window.adsbygoogle = window.adsbygoogle || [];

  try {
    window.adsbygoogle.push({});
    adElement.setAttribute("data-adsense-initialized", "true");
    return true;
  } catch (error) {
    console.warn("AdSense push failed:", error);
    return false;
  }
}

export async function pushAdSenseAdWithRetry(adElement, { maxRetries = 8, delayMs = 200 } = {}) {
  if (!adElement) {
    return false;
  }

  await ensureAdSenseScript();

  for (let attempt = 0; attempt < maxRetries; attempt += 1) {
    if (!adElement.isConnected) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
      continue;
    }

    if (pushAdSenseAd(adElement)) {
      return true;
    }

    await new Promise((resolve) => setTimeout(resolve, delayMs));
  }

  return pushAdSenseAd(adElement);
}

export async function initializeAdSenseAds(root = document) {
  await ensureAdSenseScript();

  const ads = root.querySelectorAll
    ? root.querySelectorAll("ins.adsbygoogle:not([data-adsense-initialized])")
    : [];

  await Promise.all(
    Array.from(ads).map((ad) => pushAdSenseAdWithRetry(ad))
  );
}

export function createAdSenseElement({
  adSlot,
  style,
  layoutKey,
  fullWidthResponsive = true,
}) {
  const newAd = document.createElement("ins");
  newAd.className = "adsbygoogle";

  if (style) {
    if (typeof style === "string") {
      newAd.style.cssText = style;
    } else {
      Object.assign(newAd.style, style);
    }
  }

  newAd.setAttribute("data-ad-client", ADSENSE_CLIENT);
  newAd.setAttribute("data-ad-slot", adSlot);

  if (layoutKey) {
    newAd.setAttribute("data-ad-layout-key", layoutKey);
  }

  if (fullWidthResponsive) {
    newAd.setAttribute("data-full-width-responsive", "true");
  }

  return newAd;
}

export async function mountAdSenseInContainer(container, config) {
  if (!container) {
    return;
  }

  container.innerHTML = "";
  const newAd = createAdSenseElement(config);
  container.appendChild(newAd);
  await pushAdSenseAdWithRetry(newAd);
}

export function setupAdLinkTargetHandler() {
  const handleAdClick = (event) => {
    const target = event.target.closest("a");
    if (target && target.getAttribute("target") === "_top") {
      target.setAttribute("target", "_blank");
      target.setAttribute("rel", "noopener noreferrer");
    }
  };

  document.addEventListener("click", handleAdClick);
  return () => document.removeEventListener("click", handleAdClick);
}
